import { BulkDeleteDialog } from '@/components/categories/BulkDeleteDialog'
import { BulkEditDialog } from '@/components/categories/BulkEditDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightIcon,
  Edit,
  Eye,
  EyeOff,
  Plus,
  Trash2,
} from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface CategoryListItem {
  id: string
  name: string
  slug: string
  description: string
  status: 'active' | 'inactive'
  productCount: number
  createdAt: string
  parent_id?: string
  parent?: {
    id: string
    name: string
  }
  children?: CategoryListItem[]
  level?: number
  is_root?: boolean
  display_order?: number // Added for sorting
}

interface CategoryListProps {
  className?: string
  categories: CategoryListItem[]
  isLoading?: boolean
  currentPage?: number
  total?: number
  lastPage?: number
  perPage?: number
  onPageChange?: (_page: number) => void
  onAddCategory: () => void
  onDeleteCategory: (_categoryId: string) => void
  onToggleStatus?: (_categoryId: string, _currentStatus: 'active' | 'inactive') => void
  onRefresh: () => void
}

export const CategoryList: React.FC<CategoryListProps> = ({
  className,
  categories,
  isLoading = false,
  currentPage = 1,
  total = 0,
  lastPage = 1,
  perPage = 10,
  onPageChange,
  onAddCategory,
  onDeleteCategory,
  onToggleStatus,
  onRefresh,
}) => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())

  // Dialog states
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false)
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false)
  const [isBulkOperationLoading, setIsBulkOperationLoading] = useState(false)

  // Auto-expand parent categories initially
  React.useEffect(() => {
    const parentIds = categories
      .filter((cat) => cat.children && cat.children.length > 0)
      .map((cat) => cat.id)
    setExpandedCategories(new Set(parentIds))
  }, [categories])

  // Build hierarchical tree structure from flat list
  const buildCategoryTree = (
    flatCategories: CategoryListItem[]
  ): { tree: CategoryListItem[]; childIds: Set<string> } => {
    const categoryMap = new Map<string, CategoryListItem>()
    const rootCategories: CategoryListItem[] = []
    const processedChildIds = new Set<string>()

    // First, create a map of all categories
    flatCategories.forEach((category) => {
      categoryMap.set(category.id, { ...category, children: [] })
    })

    // Then, build the tree structure
    flatCategories.forEach((category) => {
      // Skip if this category is already included as a child
      if (processedChildIds.has(category.id)) {
        return
      }

      const categoryWithChildren = categoryMap.get(category.id)!

      // If this category has children in the API response, use them
      if (category.children && category.children.length > 0) {
        categoryWithChildren.children = category.children.map((child) => {
          processedChildIds.add(child.id)
          // Get the child from map or use the child directly with its productCount
          const mappedChild = categoryMap.get(child.id) || child
          // Ensure productCount is preserved from the original child
          if (child.productCount !== undefined) {
            mappedChild.productCount = child.productCount
          }
          return mappedChild
        })
        rootCategories.push(categoryWithChildren)
      } else if (category.parent_id && categoryMap.has(category.parent_id)) {
        // This is a child category but not included in parent's children array
        const parent = categoryMap.get(category.parent_id)!
        if (!parent.children) {
          parent.children = []
        }
        parent.children.push(categoryWithChildren)
        processedChildIds.add(category.id)
      } else {
        // This is a root category without children
        rootCategories.push(categoryWithChildren)
      }
    })

    // Sort categories by display_order and name
    const sortCategories = (cats: CategoryListItem[]): CategoryListItem[] => {
      return cats
        .sort((a, b) => {
          // First by display_order if available
          if (a.display_order !== undefined && b.display_order !== undefined) {
            if (a.display_order !== b.display_order) {
              return a.display_order - b.display_order
            }
          }
          // Then by name
          return a.name.localeCompare(b.name)
        })
        .map((cat) => ({
          ...cat,
          children: cat.children ? sortCategories(cat.children) : [],
        }))
    }

    return {
      tree: sortCategories(rootCategories),
      childIds: processedChildIds,
    }
  }

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId)
      } else {
        newSet.add(categoryId)
      }
      return newSet
    })
  }

  // Render category row with indentation
  const renderCategoryRow = (category: CategoryListItem, level: number = 0): React.ReactElement => {
    const hasChildren = category.children && category.children.length > 0
    const isExpanded = expandedCategories.has(category.id)
    const indent = level * 32

    return (
      <React.Fragment key={`category-${category.id}-level-${level}`}>
        <TableRow>
          <TableCell>
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={selectedCategories.includes(category.id)}
              onChange={() => handleSelectCategory(category.id)}
            />
          </TableCell>
          <TableCell className="font-medium">
            <div className="flex items-center" style={{ paddingLeft: `${indent}px` }}>
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleCategoryExpansion(category.id)
                  }}
                  className="mr-2 p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRightIcon
                    className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  />
                </button>
              )}
              {!hasChildren && level > 0 && <span className="mr-6 text-gray-400">•</span>}
              <span>{category.name}</span>
            </div>
          </TableCell>
          <TableCell className="text-muted-foreground font-mono text-sm">{category.slug}</TableCell>
          <TableCell>
            <Badge variant={getStatusVariant(category.status)}>{category.status}</Badge>
          </TableCell>
          <TableCell>
            <span className="text-sm text-muted-foreground">{category.productCount || 0}</span>
          </TableCell>
          <TableCell>
            <span className="text-sm text-muted-foreground">{category.createdAt}</span>
          </TableCell>
          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => navigate(`/categories/${category.id}`)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onToggleStatus?.(category.id, category.status)}>
                  <EyeOff className="h-4 w-4 mr-2" />
                  {category.status === 'active' ? 'Inactive' : 'Active'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => onDeleteCategory(category.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
        {hasChildren &&
          isExpanded &&
          category.children?.map((child) => renderCategoryRow(child, level + 1))}
      </React.Fragment>
    )
  }

  // Build tree structure from all categories (not filtered) to maintain hierarchy
  const { tree: categoryTree, childIds } = buildCategoryTree(categories)

  // Filter out child categories from the main list to avoid duplication
  const parentCategoriesOnly = categories.filter((cat) => !childIds.has(cat.id))

  const handleSelectAll = () => {
    const availableParents = parentCategoriesOnly.filter((cat) =>
      cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (selectedCategories.length === availableParents.length) {
      setSelectedCategories([])
    } else {
      setSelectedCategories(availableParents.map((c) => c.id))
    }
  }

  const handleSelectCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    )
  }

  const handleBulkEdit = async (data: {
    action: string
    ids: string[]
    data?: Record<string, unknown>
  }) => {
    setIsBulkOperationLoading(true)
    try {
      await apiClient.bulkUpdateCategories({
        category_ids: data.ids,
        action: data.action as 'activate' | 'deactivate',
        data: data.data,
      })
      toast({
        title: 'Success',
        description: `Bulk ${data.action} completed successfully`,
      })
      setSelectedCategories([])
      onRefresh()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Bulk operation failed',
        variant: 'destructive',
      })
    } finally {
      setIsBulkOperationLoading(false)
    }
  }

  const handleBulkDelete = async (categoryIds: string[]) => {
    // Validate that all IDs are non-empty strings
    const validIds = categoryIds.filter((id) => id && typeof id === 'string' && id.trim() !== '')

    if (validIds.length !== categoryIds.length) {
      toast({
        title: 'Error',
        description: 'Invalid category IDs detected',
        variant: 'destructive',
      })
      return
    }

    // Prevent deletion of the Uncategorized category
    if (validIds.includes('00000000-0000-0000-0000-000000000001')) {
      toast({
        title: 'Error',
        description: 'Cannot delete the Uncategorized system category',
        variant: 'destructive',
      })
      return
    }

    setIsBulkOperationLoading(true)
    try {
      // Send all selected categories (both parent and child) for deletion
      const data = {
        category_ids: validIds,
        action: 'delete' as const,
        data: {
          move_products_to: '00000000-0000-0000-0000-000000000001', // Uncategorized category ID
        },
      }

      await apiClient.bulkUpdateCategories(data)
      toast({
        title: 'Success',
        description: `${categoryIds.length} categor${categoryIds.length > 1 ? 'ies' : 'y'} deleted successfully`,
      })
      setSelectedCategories([])
      onRefresh()
    } catch (error: unknown) {
      let errorMessage = 'Failed to delete categories'
      if (error && typeof error === 'object' && 'response' in error) {
        const responseError = error as {
          response?: { data?: { message?: string; errors?: Record<string, string[]> } }
        }

        // Handle validation errors more specifically
        if (responseError.response?.data?.errors) {
          const errors = responseError.response.data.errors
          const errorMessages = Object.values(errors).flat()
          errorMessage = errorMessages.join(', ') || errorMessage
        } else {
          errorMessage = responseError.response?.data?.message || errorMessage
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsBulkOperationLoading(false)
    }
  }

  const getStatusVariant = (status: CategoryListItem['status']) => {
    return status === 'active' ? 'default' : 'secondary'
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle>Categories</CardTitle>
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 space-x-2">
              <Input
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Button
                onClick={onAddCategory}
                size="default"
                className="h-10 min-h-[40px] flex items-center box-border"
                style={{ height: '40px' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={selectedCategories.length === 0}
                    size="default"
                    className="h-10 min-h-[40px] flex items-center box-border"
                    style={{ height: '42px' }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Bulk Actions
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowBulkEditDialog(true)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Bulk Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowBulkDeleteDialog(true)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Bulk Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Selected count indicator */}
          {selectedCategories.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedCategories.length} selected
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={
                        parentCategoriesOnly.length > 0 &&
                        parentCategoriesOnly.every((cat) => selectedCategories.includes(cat.id))
                      }
                      onChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="text-muted-foreground">Loading categories...</div>
                    </TableCell>
                  </TableRow>
                ) : parentCategoriesOnly.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="text-muted-foreground">
                        {searchTerm
                          ? `No categories found matching "${searchTerm}"`
                          : 'No categories found'}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  categoryTree.map((category) => renderCategoryRow(category))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing {Math.min((currentPage - 1) * perPage + 1, total)} to{' '}
                {Math.min(currentPage * perPage, total)} of {total} categories
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange?.(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
                    let pageNum: number
                    if (lastPage <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= lastPage - 2) {
                      pageNum = lastPage - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => onPageChange?.(pageNum)}
                        disabled={isLoading}
                        className="min-w-[2.5rem]"
                      >
                        {pageNum}
                      </Button>
                    )
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange?.(currentPage + 1)}
                  disabled={currentPage === lastPage || isLoading}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Operation Dialogs */}
      <BulkEditDialog
        open={showBulkEditDialog}
        onOpenChange={setShowBulkEditDialog}
        selectedCategories={selectedCategories}
        onBulkEdit={handleBulkEdit}
        isLoading={isBulkOperationLoading}
      />

      <BulkDeleteDialog
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
        selectedCategories={selectedCategories}
        onBulkDelete={handleBulkDelete}
        isLoading={isBulkOperationLoading}
      />
    </>
  )
}
